// SPDX-License-Identifier: SEE LICENSE
import { useEffect, useRef, useState } from 'react';

/**
 * Scale-to-fit primitive for fixed-width ("1:1 design") panels.
 *
 * The pillar detail panels are laid out at a fixed design width so their
 * fixed-px children never wrap or collide. On screens where the available
 * column is narrower than that design width those children would overlap,
 * so we measure the available width and shrink the whole panel with a CSS
 * transform instead.
 *
 * Usage:
 *   const { ref, innerRef, scale, contentHeight } = useScaleToFit(1060);
 *   <div ref={ref} style={{ width: '100%', height: contentHeight }}>
 *     <div ref={innerRef} style={{ width: 1060, transform: `scale(${scale})`,
 *                                   transformOrigin: 'top left' }}>
 *       ...panel...
 *     </div>
 *   </div>
 *
 * - `ref`  goes on the OUTER measured wrapper (fills the column, width:100%).
 *          Its content-box width is observed to compute `scale`.
 * - `innerRef` goes on the INNER design-width box. Its natural (unscaled)
 *          height is observed so the wrapper can reserve exactly the scaled
 *          height — no empty gap below, no clipping.
 * - `scale` = min(2.4, availableWidth / designWidth). Shrinks below 1 on narrow
 *          columns; grows above 1 on wide columns so the panel always fills the
 *          width (no empty gap). At exactly designWidth it's a 1.0 passthrough.
 * - `contentHeight` = innerNaturalHeight * scale, the height to reserve on the
 *          wrapper (transforms don't shrink the layout box, so we set it).
 */
export function useScaleToFit(designWidth: number): {
  ref: React.RefObject<HTMLDivElement>;
  innerRef: React.RefObject<HTMLDivElement>;
  scale: number;
  contentHeight: number | undefined;
} {
  const ref = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [innerHeight, setInnerHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    const outer = ref.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const measure = () => {
      const available = outer.clientWidth;
      // Fill the column in BOTH directions: shrink when narrower than the
      // design width (so fixed-width children never overlap) AND grow when the
      // column is wider (so wide monitors don't leave a big empty gap to the
      // right of the panel). Capped at 2.4× as a sanity bound on 4K+ displays.
      const next = available > 0 ? Math.min(2.4, available / designWidth) : 1;
      setScale(next);
      // Natural (unscaled) height of the design-width content. offsetHeight is
      // unaffected by the CSS transform, so it reflects the true layout size.
      setInnerHeight(inner.offsetHeight);
    };

    measure();

    const observer = new ResizeObserver(() => measure());
    observer.observe(outer);
    observer.observe(inner);

    return () => observer.disconnect();
  }, [designWidth]);

  return {
    ref,
    innerRef,
    scale,
    contentHeight: innerHeight != null ? innerHeight * scale : undefined,
  };
}
